<template>
  <transition name="course-sidebar-fade">
    <div v-if="drawerModel" class="course-sidebar-shell">
      <button
        class="course-sidebar-overlay"
        type="button"
        aria-label="Cerrar menu"
        @click="drawerModel = false"
      />

      <aside class="course-sidebar" role="navigation" aria-label="Contenido de curso">
        <div class="course-sidebar__header">
          <v-img
            v-if="brandLogoUrl"
            contain
            :src="brandLogoUrl"
            max-height="40"
            max-width="128"
          />
          <strong v-else class="course-sidebar__brand">Pilsa</strong>

          <v-btn icon @click="drawerModel = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <section class="course-sidebar__mobile-actions" aria-label="Acciones de la leccion">
          <button
            v-for="action in supportActions"
            :key="action.event"
            :class="[
              'course-sidebar__action',
              { 'course-sidebar__action--active': activePanel === action.event },
            ]"
            type="button"
            :aria-label="action.label"
            :title="action.label"
            :disabled="action.disabled"
            @click="emitSupportAction(action.event)"
          >
            <v-icon size="18">{{ action.icon }}</v-icon>
          </button>
        </section>

        <header class="course-sidebar__section-header">
          <button
            v-if="activePanel !== 'content'"
            class="course-sidebar__back-section"
            type="button"
            @click="showCourseContent"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
            <span>Contenido de curso</span>
          </button>
          <h2 class="course-sidebar__section-title">{{ activePanelTitle }}</h2>
        </header>

        <div v-if="activePanel === 'content'" class="course-sidebar__content">
          <div v-if="loading" class="course-sidebar__loading">
            <v-progress-circular indeterminate size="24" width="2" color="#0076ff" />
            <span>Cargando contenido...</span>
          </div>

          <div v-else-if="lessons.length === 0" class="course-sidebar__empty">
            No hay lecciones disponibles.
          </div>

          <nav v-else class="course-sidebar__module-list">
            <section
              v-for="module in moduleSections"
              :key="module.id"
              class="course-sidebar__module-section"
            >
              <h3 class="course-sidebar__module-title">{{ module.name }}</h3>
              <div class="course-sidebar__lesson-list">
                <button
                  v-for="lesson in module.lessons"
                  :key="lesson.id || lesson.globalIndex"
                  :class="[
                    'course-sidebar__lesson-item',
                    {
                      'course-sidebar__lesson-item--active': isCurrentLesson(lesson),
                      'course-sidebar__lesson-item--completed': isCompleted(lesson),
                      'course-sidebar__lesson-item--locked': isUnavailableLesson(lesson, lesson.globalIndex),
                    },
                  ]"
                  type="button"
                  :aria-label="lessonAriaLabel(lesson, lesson.globalIndex)"
                  :disabled="loadingLessonId === lesson.id || isUnavailableLesson(lesson, lesson.globalIndex)"
                  @click="goToLesson(lesson, lesson.globalIndex)"
                >
                  <span class="course-sidebar__lesson-body">
                    <strong>{{ lesson.displayIndex }}. {{ lessonTitle(lesson) }}</strong>
                  </span>
                  <v-progress-circular
                    v-if="loadingLessonId === lesson.id"
                    indeterminate
                    size="16"
                    width="2"
                    color="#0076ff"
                  />
                  <v-icon
                    v-else-if="isCompleted(lesson)"
                    class="course-sidebar__completed-icon"
                    size="18"
                  >
                    mdi-eye-outline
                  </v-icon>
                  <v-icon
                    v-else-if="isUnavailableLesson(lesson, lesson.globalIndex)"
                    class="course-sidebar__locked-icon"
                    size="18"
                  >
                    mdi-lock-outline
                  </v-icon>
                  <v-icon v-else size="16">mdi-chevron-right</v-icon>
                </button>
              </div>
            </section>
          </nav>
        </div>
        <div v-else class="course-sidebar__support-panel">
          <slot :name="activePanel" />
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CourseSidebar',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    brandLogoUrl: {
      type: String,
      default: '',
    },
    moduleName: {
      type: String,
      default: '',
    },
    lessonName: {
      type: String,
      default: '',
    },
    moduleId: {
      type: [Number, String],
      default: null,
    },
    scheduleId: {
      type: [Number, String],
      default: null,
    },
    currentLessonId: {
      type: [Number, String],
      default: null,
    },
    returnTo: {
      type: String,
      default: '',
    },
    questionsCount: {
      type: [Number, String],
      default: 0,
    },
    attachmentsCount: {
      type: [Number, String],
      default: 0,
    },
    notesCount: {
      type: [Number, String],
      default: 0,
    },
    pollsCount: {
      type: [Number, String],
      default: 0,
    },
    canOpenQuestions: {
      type: Boolean,
      default: false,
    },
    canOpenAttachments: {
      type: Boolean,
      default: false,
    },
    canOpenComments: {
      type: Boolean,
      default: false,
    },
    canOpenRating: {
      type: Boolean,
      default: false,
    },
    canOpenNotes: {
      type: Boolean,
      default: false,
    },
    canOpenPolls: {
      type: Boolean,
      default: false,
    },
    showPollsAction: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingLessonId: null,
      pendingLessons: [],
      completedLessons: [],
      orderedLessons: [],
      loaded: false,
      activePanel: 'content',
    };
  },
  computed: {
    drawerModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    lessons() {
      const seen = new Set();
      const combinedLessons = [...this.pendingLessons, ...this.completedLessons];
      const assignedLessons = combinedLessons.filter((lesson) => !this.isQuestionnaire(lesson));
      const assignedLessonsByKey = new Map(
        assignedLessons.map((lesson) => [this.lessonKey(lesson), lesson])
      );
      const sourceLessons = this.orderedLessons.length > 0 ? this.orderedLessons : assignedLessons;
      const lessonsWithAssignedData = [...sourceLessons, ...assignedLessons].map((lesson) => {
        const assignedLesson = assignedLessonsByKey.get(this.lessonKey(lesson));
        return assignedLesson ? { ...lesson, ...assignedLesson } : lesson;
      });

      return lessonsWithAssignedData.filter((lesson) => {
        if (this.isQuestionnaire(lesson)) return false;

        const key = this.lessonKey(lesson);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    nextUnlockedLessonIndex() {
      const lastCompletedIndex = this.lessons.reduce((lastIndex, lesson, index) => {
        return this.isCompleted(lesson) ? index : lastIndex;
      }, -1);

      return this.lessons.findIndex((lesson, index) => {
        return index > lastCompletedIndex && !this.isCompleted(lesson);
      });
    },
    lessonsWithPosition() {
      return this.lessons.map((lesson, index) => ({
        ...lesson,
        globalIndex: index,
        displayIndex: index + 1,
      }));
    },
    moduleSections() {
      const sections = [];
      const sectionsById = new Map();

      this.lessonsWithPosition.forEach((lesson) => {
        const moduleId = this.lessonModuleId(lesson);
        if (!sectionsById.has(moduleId)) {
          const section = {
            id: moduleId,
            name: this.lessonModuleName(lesson),
            lessons: [],
          };
          sectionsById.set(moduleId, section);
          sections.push(section);
        }

        sectionsById.get(moduleId).lessons.push(lesson);
      });

      return sections;
    },
    supportActions() {
      const actions = [
        {
          event: 'questions',
          label: 'Preguntas',
          icon: 'mdi-help-circle-outline',
          count: this.questionsCount,
          disabled: !this.canOpenQuestions,
        },
        {
          event: 'attachments',
          label: 'Adjuntos',
          icon: 'mdi-paperclip',
          count: this.attachmentsCount,
          disabled: !this.canOpenAttachments,
        },
        {
          event: 'comments',
          label: 'Comentarios',
          icon: 'mdi-comment-outline',
          count: 0,
          disabled: !this.canOpenComments,
        },
        {
          event: 'rating',
          label: 'Rating',
          icon: 'mdi-star-outline',
          count: 0,
          disabled: !this.canOpenRating,
        },
      ];

      if (this.canOpenNotes) {
        actions.push({
          event: 'notes',
          label: 'Notas',
          icon: 'mdi-note-text-outline',
          count: this.notesCount,
          disabled: false,
        });
      }

      if (this.showPollsAction) {
        actions.push({
          event: 'polls',
          label: 'Encuestas',
          icon: 'mdi-poll',
          count: this.pollsCount,
          disabled: !this.canOpenPolls,
        });
      }

      return actions;
    },
    activePanelTitle() {
      const action = this.supportActions.find((item) => item.event === this.activePanel);
      return action ? action.label : 'Contenido de curso';
    },
  },
  watch: {
    value(active) {
      if (active) {
        this.activePanel = 'content';
        this.loadLessons();
      }
    },
    moduleId() {
      this.resetLessons();
      if (this.value) {
        this.loadLessons();
      }
    },
    scheduleId() {
      this.resetLessons();
      if (this.value) {
        this.loadLessons();
      }
    },
  },
  methods: {
    resetLessons() {
      this.loaded = false;
      this.pendingLessons = [];
      this.completedLessons = [];
      this.orderedLessons = [];
    },
    emitSupportAction(action) {
      const selectedAction = this.supportActions.find((item) => item.event === action);
      if (selectedAction && selectedAction.disabled) return;

      this.activePanel = action;
      this.$emit(`open-${action}`);
    },
    showCourseContent() {
      this.activePanel = 'content';
    },
    async loadLessons() {
      if (!this.moduleId || this.loading) return;

      this.loading = true;
      try {
        const request = {
          module_id: this.moduleId,
          schedule_id: this.scheduleId,
        };
        const [pendingResponse, completedResponse, orderedResponse] = await Promise.all([
          this.$store.dispatch('profile/LIST_CONTENT_PENDING_CONTENT_BY_MODULE', request),
          this.$store.dispatch('profile/LIST_CONTENT_COMPLETE', request),
          this.$store
            .dispatch('profile/LIST_CONTENT_BY_CODE_MODULE', request)
            .catch(() => null),
        ]);

        this.pendingLessons = pendingResponse.data.data || [];
        this.completedLessons = completedResponse.data.data || [];
        this.orderedLessons = this.extractOrderedLessons(orderedResponse);
        this.loaded = true;
      } catch (error) {
        this.$snotify.error('No se pudo cargar el contenido del curso');
      } finally {
        this.loading = false;
      }
    },
    isCurrentLesson(lesson) {
      return [lesson.id, lesson.content_id]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => String(value) === String(this.currentLessonId));
    },
    lessonKey(lesson) {
      return String(lesson.content_id || lesson.id || lesson.pill_name || '');
    },
    lessonModuleId(lesson) {
      return String(
        lesson.module_id ||
          lesson.module?.id ||
          lesson.content_level_module_id ||
          this.moduleId ||
          'current-module'
      );
    },
    lessonModuleName(lesson) {
      return (
        lesson.module?.name ||
        lesson.module_name ||
        lesson.content_level_module?.module?.name ||
        this.moduleName ||
        'Modulo'
      );
    },
    extractOrderedLessons(response) {
      const content = response?.data?.data;
      if (Array.isArray(content)) return content;
      if (!content || typeof content !== 'object') return [];

      return ['basico', 'intermedio', 'avanzado'].reduce((lessons, level) => {
        const levelLessons = content[level];
        return Array.isArray(levelLessons) ? [...lessons, ...levelLessons] : lessons;
      }, []);
    },
    isCompleted(lesson) {
      const key = this.lessonKey(lesson);
      return this.completedLessons
        .filter((item) => !this.isQuestionnaire(item))
        .some((item) => this.lessonKey(item) === key);
    },
    isQuestionnaire(lesson) {
      return ['cuestionario', 'test_content'].includes(lesson.file_type);
    },
    isNextLessonAvailable(index) {
      return index === this.nextUnlockedLessonIndex;
    },
    canOpenLesson(lesson, index) {
      return (
        this.isCurrentLesson(lesson) ||
        this.isCompleted(lesson) ||
        this.isNextLessonAvailable(index)
      );
    },
    isUnavailableLesson(lesson, index) {
      return !this.canOpenLesson(lesson, index);
    },
    lessonAriaLabel(lesson, index) {
      const status = [];
      if (this.isCompleted(lesson)) status.push('visto');
      if (this.isUnavailableLesson(lesson, index)) status.push('bloqueada');

      return [this.lessonTitle(lesson), ...status].join(', ');
    },
    lessonTitle(lesson) {
      return lesson.pill_name || lesson.area_name || lesson.name || 'Leccion sin titulo';
    },
    async goToLesson(lesson, index) {
      if (this.isCurrentLesson(lesson)) {
        this.drawerModel = false;
        return;
      }
      if (this.isUnavailableLesson(lesson, index) || this.loadingLessonId) return;

      this.loadingLessonId = lesson.id;
      try {
        const response = await this.$store.dispatch('profile/ENCRYPT_CONTENT', {
          content_id: lesson.id,
        });
        const dataEncrypt = response.data.public_key;
        const encryptedValue = this.CryptoJS.AES.encrypt(
          dataEncrypt,
          'MyEncrypt-dAtA-sEcrEt'
        ).toString();

        sessionStorage.setItem('Value-rec', encryptedValue);

        const query = {
          index: response.data.data_encrypt,
          module_id: lesson.module_id || this.moduleId,
        };

        const scheduleId =
          this.scheduleId ||
          lesson.schedule_id ||
          lesson.whatsapp_accounts?.[0]?.pivot?.schedule_id;
        if (scheduleId) {
          query.schedule_id = scheduleId;
        }

        if (this.returnTo) {
          query.return_to = this.returnTo;
        }

        this.drawerModel = false;
        this.$router.push({
          name: 'show_content',
          query,
        });
      } catch (error) {
        this.$snotify.error('No se pudo abrir la leccion seleccionada');
      } finally {
        this.loadingLessonId = null;
      }
    },
  },
};
</script>

<style scoped>
.course-sidebar-shell {
  position: fixed;
  inset: 0;
  z-index: 320;
}

.course-sidebar-overlay {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(16, 24, 32, 0.28);
  cursor: pointer;
}

.course-sidebar {
  position: absolute;
  top: 0;
  right: 0;
  width: 360px;
  max-width: calc(100vw - 28px);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #101820;
  box-shadow: -12px 0 28px rgba(16, 24, 32, 0.16);
}

.course-sidebar__header {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px;
  border-bottom: 1px solid #eef2f6;
}

.course-sidebar__brand {
  font: normal normal 700 20px/28px Poppins;
}

.course-sidebar__section-header {
  display: grid;
  gap: 8px;
  padding: 16px 14px 10px;
  border-bottom: 1px solid #eef2f6;
}

.course-sidebar__section-title {
  margin: 0;
  color: #101820;
  font: normal normal 700 16px/24px Poppins;
}

.course-sidebar__back-section {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6b7785;
  cursor: pointer;
  font: normal normal 600 12px/18px Poppins;
}

.course-sidebar__back-section:hover {
  color: #0076ff;
}

.course-sidebar__back-section .v-icon {
  color: currentColor;
}

.course-sidebar__mobile-actions {
  display: none;
}

.course-sidebar__action {
  width: 100%;
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #edf1f5;
  border-radius: 8px;
  background: #f8fafc;
  color: #101820;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.course-sidebar__action:hover {
  background: #f0f7ff;
  border-color: #cfe5ff;
  color: #0076ff;
}

.course-sidebar__action--active {
  background: #f0f7ff;
  border-color: #0076ff;
  color: #0076ff;
}

.course-sidebar__action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.course-sidebar__action .v-icon {
  color: currentColor;
  font-size: 18px !important;
}

.course-sidebar__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

.course-sidebar__support-panel {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

.course-sidebar__loading,
.course-sidebar__empty {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #6b7785;
  font: normal normal 400 13px/20px Poppins;
  text-align: center;
}

.course-sidebar__module-list {
  display: grid;
  gap: 16px;
}

.course-sidebar__module-section {
  display: grid;
  gap: 10px;
}

.course-sidebar__module-title {
  margin: 0;
  color: #6b7785;
  font: normal normal 700 12px/18px Poppins;
}

.course-sidebar__lesson-list {
  display: grid;
  gap: 10px;
}

.course-sidebar__lesson-item {
  width: 100%;
  min-height: 54px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18px;
  align-items: start;
  gap: 10px;
  padding: 12px;
  border: 1px solid #edf1f5;
  border-radius: 8px;
  background: #fff;
  color: #101820;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.course-sidebar__lesson-item:hover {
  background: #f8fbff;
  border-color: #cfe5ff;
}

.course-sidebar__lesson-item--completed {
  background: #f0faf7;
  border-color: #9ddfca;
}

.course-sidebar__lesson-item--completed:hover {
  background: #e6f7f1;
  border-color: #63c9a9;
}

.course-sidebar__completed-icon {
  color: #168a6b !important;
}

.course-sidebar__lesson-item--active {
  background: #f0f7ff;
  border-color: #0076ff;
}

.course-sidebar__lesson-item--active .course-sidebar__completed-icon {
  color: #0076ff !important;
}

.course-sidebar__lesson-item--locked {
  background: #f8fafc;
  border-color: #edf1f5;
  opacity: 0.7;
  cursor: not-allowed;
}

.course-sidebar__lesson-item--locked:hover {
  background: #f8fafc;
  border-color: #edf1f5;
}

.course-sidebar__lesson-item--locked .course-sidebar__lesson-body strong {
  color: #6b7785;
}

.course-sidebar__lesson-body {
  min-width: 0;
}

.course-sidebar__lesson-body strong {
  color: #101820;
  font: normal normal 700 12px/17px Poppins;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: normal;
}

.course-sidebar__locked-icon {
  color: #8a98a5 !important;
}

.course-sidebar-fade-enter-active,
.course-sidebar-fade-leave-active {
  transition: opacity 0.2s ease;
}

.course-sidebar-fade-enter,
.course-sidebar-fade-leave-to {
  opacity: 0;
}

.course-sidebar-fade-enter-active .course-sidebar,
.course-sidebar-fade-leave-active .course-sidebar {
  transition: transform 0.22s ease;
}

.course-sidebar-fade-enter .course-sidebar,
.course-sidebar-fade-leave-to .course-sidebar {
  transform: translateX(100%);
}

@media (max-width: 640px) {
  .course-sidebar {
    width: 100%;
    max-width: none;
  }

  .course-sidebar__mobile-actions {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;
    padding: 12px 12px 8px;
  }

  .course-sidebar__section-header {
    padding-top: 10px;
  }
}

</style>
